<script setup>
import { ref, computed } from 'vue'
import {formatPrice} from '@helpers/utils'
import ListingRating from '@components/ListingRating.vue'
import ListingImageGallery from '@components/ListingImageGallery.vue'
import ListingTags from '@components/ListingTags.vue'
import ListingAddToCart from '@components/ListingAddToCart.vue'
import Icon from '@components/Icon.vue'
import AddToCartButton from '@components/AddToCartButton.vue'
import ListingQuantity from "@components/ListingQuantity.vue";

const props = defineProps({
  listing: {
    type: Object,
    required: true,
  },
  flavorOptions: {
    type: Object,
    default: () => {},
  },
})

const selectedProducts = ref([])

const bundleSize = Array.from(
    Array(props.listing?.bundle_builder_maximum || 1),
    (_, index) => index + 1,
)

const points = Math.ceil(props.listing?.products?.[0]?.price) * 5

const reachedMinimumSelectedProducts = computed(
    () => selectedProducts.value.length >= props.listing.bundle_builder_minimum
)

const reachedMaximumSelectedProducts = computed(
    () => selectedProducts.value.length >= props.listing.bundle_builder_maximum
)

const payload = computed(() => {
  let data = {}
  selectedProducts.value.forEach(product => {
    if (!data.hasOwnProperty(product.id)) {
      data[product.id] = {
        product_id: product.id,
        quantity: 0,
      }
    }

    data[product.id]['quantity'] += 1
  })
  return Object.values(data)
})

const bundleOriginalPrice = computed(() => {
  return selectedProducts.value.reduce((partialSum, a) => partialSum + Number(a?.price), 0)
})

const bundlePrice = computed(() => {
  if (selectedProducts.value.length === 0) {
    return 0
  }

  let breaks = (props.listing?.breaks || [])
      .filter(({ quantity }) => quantity <= selectedProducts.value.length)
      .sort((a, b) => (a.quantity > b.quantity ? -1 : 1))

  let discount = 1
  if (breaks.length) {
    discount = 1 - (breaks[0].discount_amount / 100)
  }

  return bundleOriginalPrice.value * discount
})
</script>

<template>
  <template v-if="listing?.bundle_builder_enabled">
    <div class="product__main bundle-builder">
      <div class="bundle-builder__products">
        <div v-for="product in listing.products" class="bundle-builder__product">
          <div v-if="product?.image" class="bundle-builder__product-image">
            <img :src="product.image.url" :alt="product.image.alt" />
          </div>

          <div class="bundle-builder__product-title">{{ product.option_value }}</div>
          <div class="bundle-builder__product-price">${{ formatPrice(product.price) }}</div>
          <button
            @click="selectedProducts.push(product)"
            class="bundle-builder__product-button account__button"
            :disabled="reachedMaximumSelectedProducts || !product.available_for_sale"
          >
            <template v-if="!product.available_for_sale">Out of Stock</template>
            <template v-else>ADD {{ listing.option_label }}</template>
          </button>
        </div>
      </div>
      <div class="bundle-builder__main">
        <div class="product__stats">
          <a href="#" class="listing__rating" id="rating">
            <ListingRating :total="listing.total_reviews" :rating="listing.rating" />
          </a>
          <div v-if="points > 0" class="product__points">
            <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none">
              <path d="M1 3.2C1 1.98497 1.98497 1 3.2 1H7V4.8C7 6.01503 6.01503 7 4.8 7H1V3.2Z" stroke="#E37F31" stroke-width="2"/>
            </svg>
            <span>
              Earns <strong>{{ points }}</strong> HERO Points
            </span>
          </div>
        </div>
        <h1 class="product__title">{{ listing.title }}</h1>
        <div class="product__excerpt" v-html="listing.excerpt"></div>
        <div class="bundle-builder__selected">
          <div class="bundle-builder__selected-title">
            Add at least {{ listing?.bundle_builder_minimum || 2 }} products to your bundle:
          </div>
          <div class="bundle-builder__selected-products">
            <div
              v-for="i in bundleSize"
              :class="['bundle-builder__selected-product', {'bundle-builder__selected-product--empty': selectedProducts.length < i}]"
            >
              <div class="bundle-builder__selected-product__grid">
                <span v-if="selectedProducts.length < i">
                  <span v-if="listing?.breaks?.find(b => b.quantity === i)">
                    ADD FOR <strong>{{ Math.round(listing?.breaks?.find(b => b.quantity === i)?.discount_amount || 0) }}% OFF</strong>
                  </span>
                  <span v-else>ADD {{ listing.option_label }}</span>
                </span>
                <div v-else>
                  <img :src="selectedProducts?.[i - 1]?.image?.url" :alt="selectedProducts?.[i - 1]?.image?.alt">
                  <div @click="selectedProducts.splice(i - 1, 1)" class="bundle-builder__selected-product__remove">
                    <Icon type="close-circle" />
                  </div>
                </div>
              </div>
              <span>{{ listing.option_label }} {{ i }}</span>
            </div>
          </div>
        </div>
      </div>
      <div class="bundle-builder__cart">
        <div class="bundle-builder__frequency">
          <span>One-Time Purchase</span>
          <span class="bundle-builder__price">
            <del v-if="bundleOriginalPrice !== bundlePrice">${{ formatPrice(bundleOriginalPrice) }}</del>
            ${{ formatPrice(bundlePrice) }}
          </span>
        </div>
        <div v-if="!reachedMinimumSelectedProducts" class="bundle-builder__error">
          Add at least {{ listing?.bundle_builder_minimum || 2 }} to proceed to cart
        </div>
        <AddToCartButton
            :class="['bundle-builder__add-to-cart', {'bundle-builder__add-to-cart--disabled': !reachedMinimumSelectedProducts}]"
            :listing="listing"
            :payload="payload"
            :disabled="!reachedMinimumSelectedProducts"
            :notify="listing.out_of_stock"
        >
          ADD TO CART
        </AddToCartButton>
      </div>
    </div>

    <div class="bundle-builder__sticky-bar" id="actionBar">
      <div v-if="!reachedMinimumSelectedProducts" class="bundle-builder__error">
        Add at least {{ listing?.bundle_builder_minimum || 2 }} to proceed to cart
      </div>
      <AddToCartButton
          :class="['bundle-builder__add-to-cart', {'bundle-builder__add-to-cart--disabled': !reachedMinimumSelectedProducts}]"
          :listing="listing"
          :payload="payload"
          :disabled="!reachedMinimumSelectedProducts"
          :notify="listing.out_of_stock"
      >
        ADD TO CART
      </AddToCartButton>
    </div>
  </template>
  <template v-else>
    <div class="product__info">
      <div class="product__stats">
        <a href="#" class="listing__rating" id="rating">
          <ListingRating :total="listing.total_reviews" :rating="listing.rating" />
        </a>
        <div v-if="points > 0" class="product__points">
          <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none">
            <path d="M1 3.2C1 1.98497 1.98497 1 3.2 1H7V4.8C7 6.01503 6.01503 7 4.8 7H1V3.2Z" stroke="#E37F31" stroke-width="2"/>
          </svg>
          <span>
            Earns <strong>{{ points }}</strong> HERO Points
          </span>
        </div>
      </div>
      <h1 class="product__title">{{ listing.title }}</h1>
      <ListingTags :listing="listing" />
      <div class="product__excerpt" v-html="listing.excerpt"></div>
      <ListingAddToCart :listing="listing" :flavorOptions="flavorOptions" client:load />
    </div>
  </template>
</template>

<style scoped lang="scss">
.bundle-builder__sticky-bar {
  display: block;
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9;
  padding: 8px 20px;
  background: var(--shades-white, #FFF);
  box-shadow: 5px 0 16px 0 rgba(0, 0, 0, 0.25);

  @media screen and (min-width: 768px) {
    display: none !important;
  }
}

.bundle-builder__error {
  border-radius: 8px;
  background: rgba(215, 88, 88, 0.10);
  color: #D75858;
  text-align: center;
  font-family: Outfit, 'Outfit Fallback', Arial, serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px; /* 142.857% */
  padding: 10px;
  margin-bottom: 6px;

  @media screen and (min-width: 768px) {
    font-size: 16px;
  }
}

:deep(.bundle-builder__add-to-cart) {
  cursor: pointer;
  width: 100%;
  border-radius: 42px;
  background: var(--Gradient-Linear-07, linear-gradient(78deg, #C76848 -0.11%, #F9C74F 99.89%));
  color: #FFF;
  text-align: center;
  font-family: Outfit, 'Outfit Fallback', Arial, serif;
  font-size: 20px;
  font-weight: 700;
  line-height: 20px; /* 100% */
  letter-spacing: 1px;
  text-transform: uppercase;
  padding: 12px;
  border: 0;

  &.bundle-builder__add-to-cart--disabled {
    background: #E4E1D7;
  }

  @media screen and (min-width: 768px) {
    border-radius: 32px;
    padding: 18px;
  }
}

.bundle-builder {
  display: grid!important;
  grid-template-columns: repeat(1, minmax(0, 1fr));
  grid-template-areas:
        "main"
        "products"
        "cart";

  @media screen and (min-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    grid-auto-rows: auto 1fr;
    align-items: flex-start;
    gap: 0 75px!important;
    grid-template-areas:
        "products main"
        "products cart";
  }

  .bundle-builder__main {
    grid-area: main;
  }

  .bundle-builder__cart {
    grid-area: cart;
    margin-top: 20px;

    .bundle-builder__frequency {
      display: flex;
      align-items: center;
      justify-content: space-between;
      border-radius: 8px;
      border: 3px solid var(--Light-navy, #183D4F);
      background: #F5F5F5;
      padding: 20px;
      color: var(--Teal, #3B717B);
      font-family: Poppins, 'Poppins Fallback', 'Trebuchet MS Fallback', Arial, serif;
      font-size: 16px;
      font-weight: 700;
      line-height: 18px; /* 112.5% */
      margin-bottom: 12px;

      @media screen and (min-width: 768px) {
        padding: 12px 16px;
        font-size: 18px;
        line-height: 32px;
        margin-bottom: 8px;
      }

      .bundle-builder__price {
        font-size: 14px;
        font-weight: 400;

        @media screen and (min-width: 768px) {
          font-size: 18px;
          font-weight: 600;
        }
      }
    }
  }

  .product__excerpt {
    border-bottom: 0!important;
    padding-bottom: 0!important;
  }

  .bundle-builder__selected {
    margin-top: 12px;

    @media screen and (min-width: 768px) {
      margin-top: 20px;
    }

    .bundle-builder__selected-title {
      color: var(--Primary-600, #183D4F);
      font-family: Poppins, 'Poppins Fallback', 'Trebuchet MS Fallback', Arial, serif;
      font-size: 16px;
      font-weight: 600;
      line-height: 32px; /* 200% */
      margin-bottom: 20px;

      @media screen and (min-width: 768px) {
        font-size: 18px;
      }
    }

    .bundle-builder__selected-products {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 16px;

      @media screen and (min-width: 768px) {
        gap: 32px;
      }

      .bundle-builder__selected-product {
        position: relative;
        aspect-ratio: 1;
        display: flex;
        flex-direction: column;
        text-align: center;
        color: var(--Neutral-1000, #3C3833);
        font-family: Poppins, 'Poppins Fallback', 'Trebuchet MS Fallback', Arial, serif;
        font-size: 13.421px;
        font-weight: 400;
        line-height: 21.66px; /* 161.391% */

        @media screen and (min-width: 768px) {
          font-size: 16px;
          line-height: 37.75px; /* 235.938% */
        }

        &.bundle-builder__selected-product--empty {
          .bundle-builder__selected-product__grid {
            padding: 20px;

            @media screen and (min-width: 768px) {
              padding: 30px;
            }
          }
        }

        .bundle-builder__selected-product__remove {
          position: absolute;
          cursor: pointer;
          top: 10px;
          right: 0;
          transform: translateX(50%);

          :deep(svg) {
            width: 20px;
            height: 20px;

            @media screen and (min-width: 768px) {
              width: 34px;
              height: 34px;
            }
          }
        }

        .bundle-builder__selected-product__grid {
          height: 100%;
          display: flex;
          text-transform: uppercase;
          justify-content: center;
          align-items: center;
          border-radius: 23px 0;
          border: 2px dashed #CCC6B8;
          margin-bottom: 9px;
          color: #CCC6B8;
          font-family: Poppins, 'Poppins Fallback', 'Trebuchet MS Fallback', Arial, serif;
          font-size: 12.377px;
          font-weight: 300;
          line-height: 14.918px; /* 120.53% */

          @media screen and (min-width: 768px) {
            font-size: 18px;
            line-height: 26px; /* 120.53% */
            margin-bottom: 16px;
          }

          img {
            width: 100%;
            display: block;
          }
        }
      }
    }
  }

  .bundle-builder__products {
    grid-area: products;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 32px;

    @media screen and (min-width: 768px) {
      gap: 54px;
    }

    .bundle-builder__product {
      font-family: Poppins, 'Poppins Fallback', 'Trebuchet MS Fallback', Arial, serif;
      color: var(--Neutral-1000, #3C3833);
      text-align: center;
      display: flex;
      flex-direction: column;
      gap: 6px;

      @media screen and (min-width: 768px) {
        gap: 16px;
      }

      .bundle-builder__product-image {
        img {
          width: 100%;
          display: block;
        }
      }

      .bundle-builder__product-title {
        font-size: 16px;
        font-weight: 700;
        line-height: 22px; /* 137.5% */

        @media screen and (min-width: 768px) {
          font-size: 20px;
          line-height: 28px;
        }
      }

      .bundle-builder__product-price {
        font-size: 12px;
        font-weight: 400;
        line-height: 14px; /* 116.667% */

        @media screen and (min-width: 768px) {
          font-size: 16px;
          line-height: 20px;
        }
      }

      .bundle-builder__product-button {
        text-transform: uppercase;
        padding: 12px 0;

        @media screen and (min-width: 768px) {
          padding: 12px 48px;
        }
      }
    }
  }
}

.product__stats {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;

  @media screen and (min-width: 768px) {
    margin-bottom: 8px;
  }
}

.product__points {
  span {
    color: var(--neutral-1000, #3C3833);
    font-size: 10px;
    font-weight: 600;
    line-height: 20px; /* 200% */
    letter-spacing: 1px;
    text-transform: uppercase;

    @media screen and (min-width: 768px) {
      font-size: 12px;
      letter-spacing: 1.2px;
      padding-left: 8px;
    }

    strong {
      font-size: 12px;
      font-weight: 600;

      @media screen and (min-width: 768px) {
        font-size: 14px;
        letter-spacing: 1.4px;
      }
    }
  }
}
.product__title {
  color: var(--neutral-1000, #3C3833);
  font-size: 28px;
  font-weight: 700;
  line-height: 32px;
  margin: 0 0 8px 0;

  @media screen and (min-width: 768px) {
    font-size: 40px;
    line-height: 48px;
  }
}
.listing__rating {
  display: inline-block;
  text-decoration: none;

  :global(.rating__text--total) {
    color: #3B717B;
    text-decoration: underline;
  }
}
.product__excerpt {
  color: var(--neutral-1000, #3C3833);
  font-size: 14px;
  font-weight: 400;
  line-height: 24px; /* 171.429% */
  padding-bottom: 16px;
  margin: 0;
  border-bottom: 1px solid #C4C3C1;

  @media screen and (min-width: 768px) {
    font-size: 16px;
    line-height: 28px
  }

  :global(ul) {
    margin-bottom: 0;
  }
}

.product__info {
  :global(.listing__tags) {
    display: flex;
    flex-direction: row-reverse;
    align-items: center;
    justify-content: flex-end;
    margin-bottom: 12px!important;
    gap: 6px;
  }

  :global(.listing__potencies) {
    margin-bottom: 0!important;
  }
}
</style>
